import { createContext, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useAgentState } from '@/context/agent-state.context';
import type { ModuleKey } from '@erp/rpc';

type ForkFromMessage = (messageId: string) => void;

/**
 * The fork action for the messages rendered inside it. Only the Nami panel
 * provides one: elsewhere (history page, project chat) a message shows no
 * fork button.
 */
export const ForkMessageContext = createContext<ForkFromMessage | undefined>(
  undefined,
);

export function useForkMessageAction(): ForkFromMessage | undefined {
  return useContext(ForkMessageContext);
}

interface ForkVariables {
  threadId: string;
  messageId: string;
  moduleKey: ModuleKey;
}

/**
 * "Fork from here" for the open conversation (AI-525): branches the thread at
 * a saved message into a new thread and switches the panel to it. Undefined
 * while there is nothing to fork or a reply is streaming, since switching
 * threads mid-reply would stop that reply.
 */
export function useForkFromMessage():
  ((messageId: string) => void) | undefined {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { currentThreadId, currentModuleKey, isLoading, loadThread } =
    useAgentState();
  const fork = useMutation({
    mutationFn: async ({ threadId, messageId }: ForkVariables) => {
      const forked = await rpcClient.ai.chatAgent.forkThread({
        threadId,
        messageId,
      });
      return rpcClient.ai.chatAgent.getThread({ threadId: forked.threadId });
    },
    onSuccess: (thread, { moduleKey }) => {
      loadThread(thread, moduleKey);
      void queryClient.invalidateQueries({
        queryKey: orpc.ai.chatAgent.listThreads.key(),
      });
    },
    onError: () => {
      toast.error(
        gt('Could not fork the conversation', {
          context: 'error toast when forking a chat conversation fails',
        }),
      );
    },
  });

  if (!currentThreadId || !currentModuleKey || isLoading || fork.isPending) {
    return undefined;
  }
  return (messageId) =>
    fork.mutate({
      threadId: currentThreadId,
      messageId,
      moduleKey: currentModuleKey,
    });
}
